import { INVENTORIZATION_BLOCK_IDS } from '@/features/inventorization/domain/inventorization';
import type { InventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import type { InventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import {
  INVENTORIZE_EXTRACTION_PATHS,
  INVENTORIZE_REGISTER_EXTRACTION_PATH_KEYS,
  type InventorizeRegisterExtractionPathKey,
} from '@/features/inventorization/domain/review/inventorize-extraction-paths';
import {
  INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS,
  INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS,
  INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS,
  INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS,
  type InventorizeRegisterReviewFieldKey,
} from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { InventorizeReviewSection } from '@/features/inventorization/domain/review/inventorize-review-field-types';
import { OCR_CANDIDATE_FIELDS, type OcrCandidateField } from '@/shared/ocr/ocr-candidates';

type StaticFieldScope = 'context' | 'identity' | 'technical';

export interface InventorizeStaticReviewFieldConfig {
  candidateField?: OcrCandidateField;
  extractionPath?: string;
  id: string;
  key: string;
  knownMeterOnly?: boolean;
  labelFallback: string;
  labelKey: string;
  remoteMountedOnly?: boolean;
  required: boolean;
  scope: StaticFieldScope;
  section: InventorizeReviewSection;
  targetBlockId: (typeof INVENTORIZATION_BLOCK_IDS)[keyof typeof INVENTORIZATION_BLOCK_IDS];
}

export interface InventorizeRegisterReviewFieldConfig {
  candidateFieldForFirstRegister?: OcrCandidateField;
  extractionKey?: InventorizeRegisterExtractionPathKey;
  field: InventorizeRegisterReviewFieldKey;
  labelFallback: string;
  labelKey: string;
  required: boolean;
}

export const INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS = [
  {
    id: INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.buildingName,
    key: 'buildingName',
    labelFallback: 'Building',
    labelKey: 'fields.building',
    required: true,
    scope: 'context',
    section: 'Location',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.context,
  },
  {
    id: INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.floorName,
    key: 'floorName',
    labelFallback: 'Floor',
    labelKey: 'fields.floor',
    required: true,
    scope: 'context',
    section: 'Location',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.context,
  },
  {
    id: INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.ownerName,
    key: 'ownerName',
    labelFallback: 'Owner',
    labelKey: 'fields.owner',
    required: false,
    scope: 'context',
    section: 'Location',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.context,
  },
  {
    id: INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.tenantName,
    key: 'tenantName',
    labelFallback: 'Tenant',
    labelKey: 'fields.tenant',
    required: false,
    scope: 'context',
    section: 'Location',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.context,
  },
] as const satisfies readonly InventorizeStaticReviewFieldConfig[];

export const INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS = [
  {
    candidateField: OCR_CANDIDATE_FIELDS.serial,
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.serial,
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.serial,
    key: 'serial',
    knownMeterOnly: true,
    labelFallback: 'Serial',
    labelKey: 'fields.serial',
    required: true,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
  {
    candidateField: OCR_CANDIDATE_FIELDS.apiKey,
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.apiKey,
    key: 'apiKey',
    labelFallback: 'QR/API key',
    labelKey: 'fields.apiKey',
    required: true,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
  {
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.commodity,
    key: 'commodity',
    labelFallback: 'Commodity',
    labelKey: 'fields.commodity',
    required: true,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
  {
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.placement,
    key: 'placement',
    labelFallback: 'Placement',
    labelKey: 'fields.placement',
    required: true,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
  {
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.newMeter,
    key: 'newMeter',
    labelFallback: 'New submeter suggestion',
    labelKey: 'fields.newSubmeterSuggestion',
    required: false,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
  {
    candidateField: OCR_CANDIDATE_FIELDS.manufacturer,
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.manufacturer,
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.manufacturer,
    key: 'manufacturer',
    knownMeterOnly: true,
    labelFallback: 'Manufacturer',
    labelKey: 'fields.manufacturer',
    required: false,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
  {
    candidateField: OCR_CANDIDATE_FIELDS.meterModel,
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.meterModel,
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.meterModel,
    key: 'meterModel',
    knownMeterOnly: true,
    labelFallback: 'Meter model',
    labelKey: 'fields.meterModel',
    required: false,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
  {
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.manufactured,
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.manufactured,
    key: 'manufactured',
    knownMeterOnly: true,
    labelFallback: 'Manufactured year',
    labelKey: 'fields.manufacturedYear',
    required: false,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
  {
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.calibrated,
    id: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.calibrated,
    key: 'calibrated',
    knownMeterOnly: true,
    labelFallback: 'Calibrated year',
    labelKey: 'fields.calibratedYear',
    required: false,
    scope: 'identity',
    section: 'Identity',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.identity,
  },
] as const satisfies readonly InventorizeStaticReviewFieldConfig[];

export const INVENTORIZE_LOCATION_TECHNICAL_REVIEW_FIELD_CONFIGS = [
  {
    id: INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.monitoredEntity,
    key: 'monitoredEntity',
    labelFallback: 'Monitored entity',
    labelKey: 'fields.monitoredEntity',
    required: true,
    scope: 'technical',
    section: 'Location',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
  },
  {
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.accessibility,
    id: INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.accessibility,
    key: 'accessibility',
    labelFallback: 'Accessibility',
    labelKey: 'fields.accessibility',
    required: true,
    scope: 'technical',
    section: 'Location',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
  },
] as const satisfies readonly InventorizeStaticReviewFieldConfig[];

export const INVENTORIZE_REMOTE_READING_REVIEW_FIELD_CONFIGS = [
  {
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.meterMountType,
    id: INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.mountType,
    key: 'mountType',
    labelFallback: 'Mount type',
    labelKey: 'fields.mountType',
    required: true,
    scope: 'technical',
    section: 'Remote reading',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
  },
  {
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.meterMountTechnology,
    id: INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.mountTechnology,
    key: 'mountTechnology',
    labelFallback: 'Mount technology',
    labelKey: 'fields.mountTechnology',
    remoteMountedOnly: true,
    required: false,
    scope: 'technical',
    section: 'Remote reading',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
  },
  {
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.remoteReadingOwner,
    id: INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.remoteReadingOwner,
    key: 'remoteReadingOwner',
    labelFallback: 'Remote reading owner',
    labelKey: 'fields.remoteReadingOwner',
    remoteMountedOnly: true,
    required: true,
    scope: 'technical',
    section: 'Remote reading',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
  },
  {
    extractionPath: INVENTORIZE_EXTRACTION_PATHS.remoteReadingSerial,
    id: INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.remoteReadingSerial,
    key: 'remoteReadingSerial',
    labelFallback: 'Remote reading serial',
    labelKey: 'fields.remoteReadingSerial',
    remoteMountedOnly: true,
    required: false,
    scope: 'technical',
    section: 'Remote reading',
    targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
  },
] as const satisfies readonly InventorizeStaticReviewFieldConfig[];

export const INVENTORIZE_NOTE_REVIEW_FIELD_CONFIG = {
  id: INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.note,
  key: 'note',
  labelFallback: 'Internal note',
  labelKey: 'fields.internalNote',
  required: false,
  scope: 'technical',
  section: 'Note',
  targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
} as const satisfies InventorizeStaticReviewFieldConfig;

export const INVENTORIZE_REGISTER_REVIEW_FIELD_CONFIGS: readonly InventorizeRegisterReviewFieldConfig[] = [
  {
    extractionKey: INVENTORIZE_REGISTER_EXTRACTION_PATH_KEYS.label,
    field: INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS.label,
    labelFallback: '{register} label',
    labelKey: 'register.labelField',
    required: false,
  },
  {
    candidateFieldForFirstRegister: OCR_CANDIDATE_FIELDS.registerCounter,
    extractionKey: INVENTORIZE_REGISTER_EXTRACTION_PATH_KEYS.value,
    field: INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS.counter,
    labelFallback: '{register} counter',
    labelKey: 'register.counterField',
    required: true,
  },
  {
    candidateFieldForFirstRegister: OCR_CANDIDATE_FIELDS.registerUnit,
    extractionKey: INVENTORIZE_REGISTER_EXTRACTION_PATH_KEYS.unit,
    field: INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS.unit,
    labelFallback: '{register} unit',
    labelKey: 'register.unitField',
    required: true,
  },
  {
    candidateFieldForFirstRegister: OCR_CANDIDATE_FIELDS.registerObis,
    extractionKey: INVENTORIZE_REGISTER_EXTRACTION_PATH_KEYS.obis,
    field: INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS.obis,
    labelFallback: '{register} OBIS',
    labelKey: 'register.obisField',
    required: true,
  },
  {
    extractionKey: INVENTORIZE_REGISTER_EXTRACTION_PATH_KEYS.tariff,
    field: INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS.tariff,
    labelFallback: '{register} tariff',
    labelKey: 'register.tariffField',
    required: false,
  },
] as const;

export function readInventorizeStaticFieldValue(
  config: InventorizeStaticReviewFieldConfig,
  input: {
    context?: InventorizationContext;
    identity?: InventorizationIdentity;
    technical?: InventorizationTechnicalDetails;
  },
) {
  const source =
    config.scope === 'context' ? input.context : config.scope === 'identity' ? input.identity : input.technical;
  const value = (source as Record<string, unknown> | undefined)?.[config.key];

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return typeof value === 'boolean' ? String(value) : String(value ?? '');
}
